import React from "react";
import { Modal, Box, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import Title from "./Title";

const StyledModal = styled(Modal)({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: 24,
  // A small gap from the top; the modal renders ABOVE the AppBar (see zIndex
  // below) so its backdrop dims the header — no overlap/blend with it.
  paddingTop: "5vh",
});

const ModalContent = styled(Box)<{ custom: string }>(({ theme, custom }) => ({
  position: "relative",
  backgroundColor: "#fff",
  borderRadius: 16,
  border: "1px solid #ECEFF3",
  boxShadow: "0 24px 60px rgba(16,24,40,0.18)",
  padding: theme.spacing(3, 3.5),
  width: "100%",
  maxWidth: 920,
  height: custom,
  // Leave room for the top gap + bottom padding so the card stays fully on-screen.
  maxHeight: "calc(100vh - 88px)",
  overflowY: "auto",
  outline: "none",
  "&::-webkit-scrollbar": { width: 10 },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#D0D5DD",
    borderRadius: 8,
    border: "3px solid #fff",
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(1.5),
  top: theme.spacing(1.5),
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.level1,
  "&:hover": { backgroundColor: theme.palette.background.level2 },
}));

type GlobalModalProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  height?: string;
};

export const GlobalModal = ({
  open,
  onClose,
  title,
  children,
  height = "85%",
}: GlobalModalProps) => {
  return (
    <StyledModal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      // Above the AppBar (zIndex drawer+1) so the modal + backdrop cover the
      // header instead of the header overlapping the modal's top edge.
      sx={{ zIndex: (theme) => theme.zIndex.modal }}
    >
      <ModalContent custom={height}>
        <CloseButton onClick={onClose}>
          <CloseIcon />
        </CloseButton>
        {title && <Title>{title}</Title>}
        <div id="modal-description">{children}</div>
      </ModalContent>
    </StyledModal>
  );
};
